import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Users, FileText } from 'lucide-react';
import { useOrganizationAnalytics } from '@/hooks/peo-organizations/useOrganizationAnalytics';

interface AnalyticsTabProps {
  providerId: string;
}

export const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ providerId }) => {
  const { analytics, isLoading } = useOrganizationAnalytics(providerId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-8 bg-muted rounded animate-pulse mb-2"></div>
                <div className="h-4 bg-muted rounded animate-pulse"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Application Trends */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Application & Quote Trends (Last 6 Months)
          </CardTitle>
          <CardDescription>Track monthly application and quote activity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {analytics.applicationTrends.map((trend, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                <span className="text-sm font-medium">{trend.month}</span>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">{trend.applications} applications</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <span className="text-sm">{trend.quotes} quotes</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quote Conversion</CardTitle>
            <CardDescription>Success rate of quotes to acceptance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {analytics.quoteConversionRate}%
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Based on accepted quotes
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Total Applications</CardTitle>
            <CardDescription>Applications created by this PEO</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {analytics.applicationTrends.reduce((sum, trend) => sum + trend.applications, 0)}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Last 6 months
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Total Quotes</CardTitle>
            <CardDescription>Quotes generated by this PEO</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {analytics.applicationTrends.reduce((sum, trend) => sum + trend.quotes, 0)}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Last 6 months
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Member Growth & Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Member Growth
            </CardTitle>
            <CardDescription>Team growth over the last 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.memberGrowth.map((growth, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm font-medium">{growth.month}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{growth.members} members</Badge>
                    {index > 0 && (
                      <div className="flex items-center">
                        {growth.members >= analytics.memberGrowth[index - 1]?.members ? (
                          <TrendingUp className="h-3 w-3 text-green-500" />
                        ) : (
                          <TrendingDown className="h-3 w-3 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Performers</CardTitle>
            <CardDescription>Most active team members</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.topPerformers.length > 0 ? (
                analytics.topPerformers.map((performer, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div>
                      <div className="font-medium text-sm">{performer.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {performer.applications} apps • {performer.quotes} quotes
                      </div>
                    </div>
                    <Badge variant="secondary">
                      #{index + 1}
                    </Badge>
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground">
                  No performance data available yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};